import { capitalize, keys, reduce } from "lodash";
import Tooltip from "rc-tooltip";
import React, { useMemo } from "react";
import Hex from "../components/Hex";
import { calculatePercentChange } from "../components/PercentageChange";
import { useStats } from "../context/stats";
import { Hotspot } from "./types";

export const classificationRgbColorMap: {
  [classification: string]: [number, number, number];
} = {
  excellent: [50, 196, 141],
  good: [52, 211, 153],
  mediocre: [252, 201, 69],
  poor: [254, 160, 83],
  terrible: [232, 97, 97],
  inactive: [124, 136, 187],
  disabled: [156, 163, 175]
};

export const classificationColorMap = reduce(
  classificationRgbColorMap,
  (
    acc: { [classification: string]: string },
    rgb: [number, number, number],
    classification: string
  ) => ({ ...acc, [classification]: `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})` }),
  {}
);

export const classificationBoundaries: {
  [classification: string]: [number, number];
} = {
  excellent: [0.95, 1],
  good: [0.75, 0.95],
  mediocre: [0.5, 0.75],
  poor: [0.25, 0.5],
  terrible: [0, 0.25],
  inactive: [0, 0]
};

export const relativePerformanceClassifications = keys(
  classificationBoundaries
).reverse();

export const alternativeRelativePerformanceTitles: {
  [classification: string]: string;
} = {
  inactive: "zero",
  terrible: "bottom 25%",
  poor: "below average",
  mediocre: "above average",
  good: "top 25%",
  excellent: "top 5%"
};

export const getClassification = (value: number) => {
  if (value >= classificationBoundaries.excellent[0]) {
    return "excellent";
  } else if (value >= classificationBoundaries.good[0]) {
    return "good";
  } else if (value >= classificationBoundaries.mediocre[0]) {
    return "mediocre";
  } else if (value >= classificationBoundaries.poor[0]) {
    return "poor";
  } else if (value === 0) {
    return "inactive";
  } else {
    return "terrible";
  }
};

interface ClassificationValueProps {
  value: number;
  hexProps?: { width: number; height: number } | undefined;
  withoutTransmitScaleTooltip?: boolean;
  alternativeContent?: React.ReactNode;
}

export const ClassificationValue: React.FC<ClassificationValueProps> = ({
  value,
  hexProps = {
    width: 10.5,
    height: 12
  },
  withoutTransmitScaleTooltip,
  alternativeContent
}) => {
  const { stats } = useStats();
  const classification = getClassification(value);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const percentageDifferenceToGlobalAvg = useMemo(
    () => calculatePercentChange(value, stats?.averageTransmitScale || 0),
    [value, stats?.averageTransmitScale]
  );

  const renderTooltipContent = useMemo(
    () => (
      <>
        <div>
          {capitalize(
            `${classification} transmit scale: ${classificationBoundaries[classification][0]} to ${classificationBoundaries[classification][1]}`
          )}
        </div>
        <div>{`This transmit scale is ${Math.abs(
          percentageDifferenceToGlobalAvg
        ).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}% ${
          percentageDifferenceToGlobalAvg > 0 ? "more" : "less"
        } than the global avg of ${(
          stats?.averageTransmitScale || 0
        ).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`}</div>
      </>
    ),
    [
      classification,
      percentageDifferenceToGlobalAvg,
      stats?.averageTransmitScale
    ]
  );

  const renderContent = useMemo(
    () => (
      <div className="flex items-center">
        <span className="flex items-center justify-center">
          <Hex
            fillColor={classificationColorMap[classification]}
            {...hexProps}
          />
        </span>

        <p className="ml-2">
          {alternativeContent ||
            (value || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
        </p>
      </div>
    ),
    [alternativeContent, classification, hexProps, value]
  );

  if (withoutTransmitScaleTooltip) return renderContent;

  return (
    <Tooltip placement="left" overlay={renderTooltipContent}>
      {renderContent}
    </Tooltip>
  );
};

export const formatHotspotPlace = (place: string | undefined | null) =>
  place || "Unknown location";

export const formatHotspotName = (hotspot: Hotspot) =>
  hotspot?.name?.split("-").map(capitalize).join(" ");
